import {
  CloudOffline,
  Collaborate,
  ConnectionSignal,
  ConnectionSignalOff,
  Rotate,
} from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { Body, Icon } from 'vet-bones/design-system/components';
import { isOnPremise, usePortalUser } from 'vet-bones/utils/portal';

import { CloudChecked } from 'src/components/icons/CloudChecked';
import { LaptopChecked } from 'src/components/icons/LaptopChecked';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useCollaborationStatus } from 'src/utils/hooks/useCollaborationStatus';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectNavbar/projectNavbar.module.scss';

const CollaborationStatus: React.VFC<{ projectId: string }> = ({
  projectId,
}) => {
  const collabStatus = useCollaborationStatus(projectId);

  if (collabStatus === 'connected') {
    return (
      <div
        className={classNames(
          styles.projectNavbarSaveState,
          styles.projectNavbarCollabConnected
        )}
      >
        <Icon IconComponent={ConnectionSignal} size="md" />
      </div>
    );
  }

  if (collabStatus === 'connecting') {
    return (
      <div
        className={classNames(
          styles.projectNavbarSaveState,
          styles.projectNavbarCollabConnecting
        )}
      >
        <Icon IconComponent={Collaborate} size="md" />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.projectNavbarSaveState,
        styles.projectNavbarCollabDisconnected
      )}
    >
      <Icon IconComponent={ConnectionSignalOff} size="md" />
    </div>
  );
};

export const ProjectNavbarSaveState: React.VFC = () => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const portalUser = usePortalUser();
  const isCloudStorageEnabled = portalUser.data?.is_designer_storage_enabled;

  const isSaving = useReduxSelector((state) => state.ui.isSavingProject);
  const isLaunchpad = isOnPremise();
  const collabStatus = useCollaborationStatus(projectId);

  if (collabStatus) {
    return <CollaborationStatus projectId={projectId} />;
  }

  if (project?.isCloudProject) {
    if (!isSaving) {
      return (
        <div
          className={classNames(
            styles.projectNavbarSaveState,
            styles.projectNavbarSaveStateFade
          )}
        >
          <Icon IconComponent={CloudChecked} size="md" />
          <Body size="xs">
            {copy.components.project.navbar.saveState.saved(isLaunchpad)}
          </Body>
        </div>
      );
    }
    return (
      <div className={styles.projectNavbarSaveState}>
        <Icon IconComponent={Rotate} size="md" />
        <Body size="xs">{copy.components.project.navbar.saveState.saving}</Body>
      </div>
    );
  }

  if (project?.cloudProjectId) {
    return (
      <div className={styles.projectNavbarSaveState}>
        <Icon IconComponent={CloudOffline} size="md" />
        <Body size="xs">
          {copy.components.project.navbar.saveState.offline}
        </Body>
      </div>
    );
  }

  return isCloudStorageEnabled ? (
    <div
      key={Date.now()}
      className={classNames(
        styles.projectNavbarSaveState,
        styles.projectNavbarSaveStateFade
      )}
    >
      <Icon IconComponent={LaptopChecked} size="md" />
      <Body size="xs">
        {copy.components.project.navbar.saveState.locallySaved}
      </Body>
    </div>
  ) : null;
};
